import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { ImpressionsDataService, ImpressionsColumnsService } from '../../services';
import { DialogImpressionFormComponent } from './impression-form/impression-form.component';
import { DialogImpressionDetailsComponent } from './impression-details/impression-details.component';
import { Impression, ImpressionsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-impressions',
  templateUrl: './impressions.component.html',
  styleUrls: ['./impressions.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class ImpressionsComponent implements OnInit, OnDestroy {

  private impressionAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private impressionEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private impressionDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public impressionToDelete: Impression = {};
  public impressions: Impression[] = [];
  public impression: Impression = {};
  public selectedImpressions: Impression[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private impressionsDataService: ImpressionsDataService,
    private impressionsColumnsService: ImpressionsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('ImpressionsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Impressions - Management');
    this.primengConfig.ripple = true;
    this.getImpressions();
  }

  ngOnDestroy(): void {
    console.log('ImpressionsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getImpressions(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.impressionsDataService.getImpressions(this.parameters).subscribe((response: ImpressionsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.impressions = response.results;
        } else {
          this.impressions = this.impressions.concat(response.results);
        }
        if (response.inlineCount > this.impressions.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getImpressions();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('IMPRESSIONSNOTLOADED')});
    });
  }

  importImpressions(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.impressionsDataService.addImpressions(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('IMPRESSIONSCREATED')});
                if (proceed === j) {
                  this.getImpressions();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('IMPRESSIONSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('IMPRESSIONSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.impressionsDataService.updateImpressions(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('IMPRESSIONSUPDATED')});
                if (proceed === j) {
                  this.getImpressions();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('IMPRESSIONSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('IMPRESSIONSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.impressionsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('impressions.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedImpressions() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Impressions?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.impressionsDataService.deleteImpressions(this.selectedImpressions).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Impressions Deleted', life: 3000});
            this.getImpressions();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Impressions not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Impressions not deleted'});
        });
        this.selectedImpressions = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.impressions, this.columns, 'Impressions');
  }

  exportExcel() {
    ToolsService.exportExcel(this.impressions, this.columns, 'Impressions');
  }

  exportCsv() {
    ToolsService.exportCsv(this.impressions, this.columns, 'Impressions');
  }

  refresh() {
    this.impressionsDataService.clearCache();
    this.getImpressions();
  }

  impressionAdd(): void {
    if (!this.navigation.getParamValue('impressions.addInDialog', true)) {
      this.router.navigate(['/impressions/add']);
      return;
    }
    this.impressionAddDialog = this.dialogService.open(DialogImpressionFormComponent, {
      header: '', // this.translate.instant('NEWIMPRESSION'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        impression: {id: null},
        visits: this.impressionsColumnsService.visits,
        banners: this.impressionsColumnsService.banners,
        users: this.impressionsColumnsService.users,
      },
    });

    this.impressionAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Impression added'});
        this.getImpressions();
      }
    });
  }

  impressionEdit(impression: Impression): void {
    if (!this.navigation.getParamValue('impressions.editInDialog', true)) {
      this.router.navigate(['/impressions/edit/' + impression.id]);
      return;
    }
    this.impressionEditDialog = this.dialogService.open(DialogImpressionFormComponent, {
      header: '', // this.translate.instant('EDITIMPRESSION') + ' ' + impression.id,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        impression: impression,
        visits: this.impressionsColumnsService.visits,
        banners: this.impressionsColumnsService.banners,
        users: this.impressionsColumnsService.users,
      },
    });

    this.impressionEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Impression updated'});
        this.getImpressions();
      }
    });
  }

  impressionDetails(impression: Impression): void {
    if (!this.navigation.getParamValue('impressions.detailsInDialog', true)) {
      this.router.navigate(['/impressions/details/' + impression.id]);
      return;
    }
    this.impressionDetailsDialog = this.dialogService.open(DialogImpressionDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        impression: impression,
      }
    });
  }

  impressionDelete(impression: Impression): void {
    this.impressionToDelete = impression;
    this.messageService.clear();
    this.messageService.add({
      key: 'impressionDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + impression.id + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.impressionsDataService.deleteImpression(this.impressionToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Impression deleted'});
      this.getImpressions();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Impression not deleted'});
    });
  }

  onDeleteReject(): void {
    this.impressionToDelete = {};
    this.messageService.clear('impressionDelete');
  }

}
